import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Breadcrumb,
  Form,
  Modal,
  Row,
  Spin,
  Tabs,
} from "antd";
import { SettingOutlined, UsergroupDeleteOutlined } from "@ant-design/icons";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs } from "../../constants/userRoles";
import {
  fetchCompanies,
  fetchCompanyStaffs,
} from "../../redux/actions/companyActions";
import lowerCase from "lodash/lowerCase";
import get from "lodash/get";

import StaffForm from "../../components/staff/StaffForm";
import requireAuth from "../../hocs/requireAuth";
import { fetchConfig } from "../../redux/actions/authActions";
import HeaderSelector from "../../components/staff/headerSelector";
import StaffTable from "../../components/staff/staffTable";
import dayjs from "dayjs";
import { getCurrentDateRange } from "../../utilities/report";
import StaffAttendanceTab from "../../components/staff/staffAttendanceTab";


const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <SettingOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <UsergroupDeleteOutlined style={{ fontSize: "16px" }} />
              <span>Staff</span>
            </>
          ),
        },
        {
          title: "Settings",
        },
      ]}
    />
  );
};

const initialState = {
  company: undefined,
};

const initialStaffState = { data: [], filtered: [], isLoaded: false, filters: { searchQuery: undefined, role: undefined, date:[getCurrentDateRange("month",dayjs())] } }
const StaffSettingScreen = () => {
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const [form] = Form.useForm();
  const [filter, setFilters] = useState(initialState);
  const [config, setConfig] = useState(undefined);
  const [selectedCompany, setSelectedCompany] = useState(undefined);
  const [staffs, setStaff] = useState(initialStaffState);
  const [staffModal, setStaffModal] = useState({
    visible: false,
    data: undefined,
  });
  const dispatch = useDispatch();

  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
    });
    form.setFieldsValue({
      Company: get(company, "id"),
    });
    setSelectedCompany(get(company, "id"));
  };

    const getConfig = () => {
    dispatch(fetchConfig("staff_types"))
      .then((result) => {
        setConfig((config)=>{
          return {...config, staff_types:get(result,'staff_types')}
        })
      })
      .catch(() => {});
  };

  const getStaffRoles = () => {
    dispatch(fetchConfig("staff_roles"))
      .then((result) => {
        setConfig((config) => {
          return { ...config, staff_roles: get(result, "staff_roles") }
        });
        getConfig()
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (get(result, "length") === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ ...config, companies: result });
        }
        getStaffRoles();
      })
      .catch((error) => {
        console.log(error)
      }).finally(() => {
        setLoading(false)
      });

  };

  const toggleStaffModal = (data = undefined) => {
    setStaffModal({
      visible: !get(staffModal, "visible"),
      data,
    });
  };

  const getCompanyStaff = (query) => {
    const q = {date:[get(query, "from_date"),get(query, "to_date")]}
    setLoading(true);
    dispatch(fetchCompanyStaffs(get(filter, "company.id"), {month:dayjs(get(query, "to_date")).format("YYYY-MM")}))
      .then((result) => {
        setLoading(true);
        setStaff({
          data: result,
          filtered: result,
          isLoaded: true,
          filters: {...get(staffs, "filters"),...q}
        });
        setShowEmpty(result.length === 0 || !result || !result.length);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const applyFilters = (baseData, filters) => {
    let filtered = [...baseData];

    if (get(filters, 'searchQuery')) {
      filtered = filtered.filter((item) =>
        lowerCase(get(item, "name", "")).includes(lowerCase(get(filters, 'searchQuery')))
      );
    }

    if (get(filters, 'role')) {
      filtered = filtered.filter(
        (item) => Number(get(item, "role.value")) === Number(get(filters, 'role'))
      );
    }

    return filtered;
  };

  useEffect(() => {
    getCompanies();
    getConfig()
  }, []);
  
  useEffect(() => {
    if (staffs.data.length > 0) {
      const filtered = applyFilters(get(staffs, 'data'), get(staffs, 'filters'));
      setStaff({ ...staffs, filtered });
    }
  }, [get(staffs, "filters")]);
  
  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <div>
          <PageBreadcrumb />
        </div>
        <div></div>
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Spin spinning={loading}>
          <Form form={form} className="w-full px-8 pt-6">
            <Row gutter={16}>
              <HeaderSelector setSelectedCompany={setSelectedCompany} filter={filter} getCompanyStaff={getCompanyStaff} setFilters={setFilters} config={config} setStaff={setStaff} staffs={staffs} form={form} />
            </Row>
          </Form>
          {get(staffs, "isLoaded") && (
            <>
              <div className="px-8 py-8">
                <Tabs
                  defaultActiveKey="1"
                  items={[{
                    label: `Staff Setting`,
                    key: 'staff_setting1',
                    children: <StaffTable inputRef={inputRef} config={config} staffs={staffs} showEmpty={showEmpty} toggleStaffModal={toggleStaffModal} setStaff={setStaff} getCompanyStaff={getCompanyStaff} filter={filter} />,
                  },
                  {
                    label: `Staff Attendance`,
                    key: 'staff_setting2',
                    children: <StaffAttendanceTab config={config} staffs={staffs} showEmpty={showEmpty} setStaff={setStaff} filter={filter} setLoading={setLoading} />,
                  }
                  ]}
                />
              </div>
            </>
          )}
          <Modal
            title={
              get(staffModal, "data", undefined) ? "Edit Staff" : "Add Staff"
            }
            open={get(staffModal, "visible")}
            destroyOnHidden
            footer={false}
            onOk={() => { }}
            onCancel={() => {
              toggleStaffModal();
            }}
          >
            <StaffForm
              toggle={() => {
                toggleStaffModal();
              }}
              initialValue={get(staffModal, "data")}
              companyUuid={selectedCompany}
              filter={filter}
              staffRoles={get(config, "staff_roles")}
              config={config}
              staffs={get(staffs, "data")}
              onSuccess={() => {
                toggleStaffModal();
                getCompanyStaff();
              }}
            />
          </Modal>
        </Spin>
      </div>
    </div>
  );
};

export default requireAuth(StaffSettingScreen, [
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs
]);
